import { useEffect, useMemo, useState } from 'react'
import {
  Gift, Save, RefreshCw, Plus, Trash2, AlertTriangle, Coins, PlayCircle,
} from 'lucide-react'
import {
  C, Card, Row, BigStat, Btn, Pill, Chips, Toggle, Loading, ErrorLine, Empty,
  inputStyle, selectStyle, labelStyle, divider, useAsync,
  fmtDateTime, fmtMoney, fmtInt,
} from './adminUI'
import { getAdminFortune, saveAdminFortune, retryFortunePayouts } from '../../services/api'

// Раздел «Колесо фортуны».
//
// Один экран на всё, что просил владелец: включение, порог резерва ⭐, срок
// хранения истории, три колеса (цена прокрута и доля возврата), призы
// (добавить/убрать/выключить), статистика, последние выигрыши и очередь
// денежных выплат.
//
// Экономика: сумма k включённых призов не может превышать 0.995 — тогда в
// среднем юзеру возвращается не больше 99.5% его доли возврата, и бот не
// уходит в минус. Сервер проверяет это ещё раз; здесь мы показываем сумму
// заранее, чтобы не сохранять заведомо отклонённый конфиг.

const TIER_IDS = ['base', 'mid', 'adv']
const TIER_LABEL = { base: 'Базовое', mid: 'Среднее', adv: 'Продвинутое' }
const K_SUM_MAX = 0.995
const KIND_LABEL = { demo: 'Демо', real: 'Реал', money: 'Деньги' }

// Цена прокрута в ⭐. Пусто и 0 — «авто» (цена считается из долларов);
// иначе целое от 1 до 10000. Раньше 0 из авто-режима уходил на сервер как
// есть и сохранение падало с bad_tier_stars — блокировалась вся админка.
function parseStarsInput(v) {
  if (v === '' || v == null) return { ok: true, value: null }
  const n = Number(v)
  if (n === 0) return { ok: true, value: null }
  if (!Number.isInteger(n) || n < 1 || n > 10000) return { ok: false, value: null }
  return { ok: true, value: n }
}

const starsErrorText = (id) =>
  `Цена прокрута в ⭐ у колеса «${TIER_LABEL[id] || id}»: нужно целое число от 1 до 10000. Пустое поле или 0 — цена считается из долларов.`

// Сервер отвечает кодами вида bad_tier_stars:mid — показываем их по-русски.
function humanSaveError(msg) {
  const m = String(msg || '')
  let g = m.match(/^bad_tier_stars:(\w+)$/)
  if (g) return starsErrorText(g[1])
  g = m.match(/^bad_tier_usd:(\w+)$/)
  if (g) return `Цена в $ у колеса «${TIER_LABEL[g[1]] || g[1]}»: от 0.01 до 100.`
  g = m.match(/^bad_tier_share:(\w+)$/)
  if (g) return `Доля возврата у колеса «${TIER_LABEL[g[1]] || g[1]}»: от 0 до 1.`
  g = m.match(/^duplicate_prize:(.+)$/)
  if (g) return `Приз «${g[1]}» повторяется в списке — id должен быть уникальным.`
  g = m.match(/^bad_prize_(k|w|usd|days):(.+)$/)
  if (g) {
    const what = { k: 'k — от 0 до 1', w: 'вес — больше 0 и до 100', usd: 'сумма — от $0.01 до $1000', days: 'срок — целое от 1 до 365 дней' }[g[1]]
    return `Приз «${g[2]}»: ${what}.`
  }
  if (m === 'k_sum_too_big') return 'Сумма k включённых призов больше 0.995 — уменьшите k у призов.'
  if (m === 'bad_history_days') return 'История: целое число дней от 1 до 365.'
  if (m === 'bad_threshold') return 'Порог резерва: целое число звёзд, не отрицательное.'
  if (m === 'bad_prize_id') return 'id приза: только латиница, цифры, «_» и «-», до 40 символов.'
  if (m === 'bad_prize_kind') return 'Тип приза: демо, реал или деньги.'
  if (m === 'empty_prizes') return 'Нужен хотя бы один приз у каждого колеса.'
  return msg || 'Не удалось сохранить настройки колеса'
}

const numInput = {
  ...inputStyle,
  width: 76, padding: '7px 8px', fontSize: 12.5, textAlign: 'right',
}
const smallSelect = {
  ...selectStyle,
  width: 96, padding: '7px 8px', fontSize: 12.5,
}

const clone = (o) => JSON.parse(JSON.stringify(o || {}))
const round4 = (n) => Math.round((Number(n) || 0) * 10000) / 10000

function prizeTitle(p) {
  if (p.kind === 'money') return `$${Number(p.usd || 0)} звёздами`
  return `+${p.days} дн. · ${p.kind === 'real' ? 'реал' : 'демо'}`
}

const STATUS_COLOR = {
  pending: C.warn,
  paid: C.accent,
  blocked: C.danger,
  failed: C.danger,
  done: C.accent,
}

export default function FortuneSection() {
  const { data, loading, error, reload } = useAsync(() => getAdminFortune(), [])
  const [draft, setDraft] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [saved, setSaved] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const [retryNote, setRetryNote] = useState('')
  // Какое колесо редактируем: у каждого СВОЙ список призов (решение владельца
  // 2026-10-20). Переключили колесо — сменился и список под ним.
  const [prizeTier, setPrizeTier] = useState('base')

  useEffect(() => {
    if (data?.config) setDraft(clone(data.config))
  }, [data])

  // Список призов выбранного колеса.
  const prizeList = draft?.tiers?.[prizeTier]?.prizes || []

  const sumK = useMemo(() => {
    return round4(prizeList.reduce((a, p) => a + (p.enabled ? Number(p.k) || 0 : 0), 0))
  }, [prizeList])

  // Σk каждого колеса: сохранять нельзя, если хоть одно перебирает.
  const sumByTier = useMemo(() => {
    const out = {}
    for (const id of TIER_IDS) {
      const list = draft?.tiers?.[id]?.prizes || []
      out[id] = round4(list.reduce((a, p) => a + (p.enabled ? Number(p.k) || 0 : 0), 0))
    }
    return out
  }, [draft])
  const kOk = sumK <= K_SUM_MAX + 1e-9
  const anyKOvers = TIER_IDS.some(id => sumByTier[id] > K_SUM_MAX + 1e-9)

  if (loading && !draft) return <Loading />
  if (error && !draft) return <ErrorLine onRetry={reload}>{error}</ErrorLine>
  if (!draft) return <Empty>Конфиг колеса не загрузился.</Empty>

  const set = (key, value) => {
    setSaved(false)
    setDraft(d => ({ ...d, [key]: value }))
  }

  const setTier = (id, key, value) => {
    setSaved(false)
    setDraft(d => ({
      ...d,
      tiers: { ...d.tiers, [id]: { ...d.tiers[id], [key]: value } },
    }))
  }

  // Правки призов идут только в список ВЫБРАННОГО колеса — остальные не
  // трогаются. Так «Свой список у каждого» и работает.
  const setPrizeList = (fn) => {
    setSaved(false)
    setDraft(d => {
      const cur = d.tiers?.[prizeTier] || {}
      return {
        ...d,
        tiers: { ...d.tiers, [prizeTier]: { ...cur, prizes: fn(cur.prizes || []) } },
      }
    })
  }

  const patchPrize = (idx, key, value) => {
    setPrizeList(list => list.map((p, i) => (i === idx ? { ...p, [key]: value } : p)))
  }

  const addPrize = () => {
    if (prizeList.length >= 30) return
    const id = `prize_${Date.now().toString(36)}`
    setPrizeList(list => [...list, {
      id, kind: 'demo', days: 1, k: 0.05, w: 1, enabled: true, jack: false,
    }])
  }

  const removePrize = (idx) => {
    if (prizeList.length <= 1) return
    setPrizeList(list => list.filter((_, i) => i !== idx))
  }

  // «Призы по цене колеса»: берёт список базового колеса и увеличивает сроки и
  // суммы во столько раз, во сколько это колесо дороже (среднее ×3, продвинутое
  // ×6). k и w не меняются — Σk и экономика остаются прежними. Кнопка нужна,
  // если у колеса уже сохранён свой (такой же, как у соседей) список.
  const scaleFromBase = () => {
    const base = draft?.tiers?.base?.prizes || []
    const baseUsd = Number(draft?.tiers?.base?.usd) || 0
    const usd = Number(draft?.tiers?.[prizeTier]?.usd) || 0
    if (!base.length || !(baseUsd > 0) || !(usd > 0)) return
    const ratio = usd / baseUsd
    setPrizeList(() => base.map(p => {
      const out = { ...p }
      if (p.kind === 'money') {
        out.usd = Math.min(1000, Math.max(0.01, Math.round(Number(p.usd) * ratio * 100) / 100))
      } else {
        out.days = Math.min(365, Math.max(1, Math.round((parseInt(p.days, 10) || 1) * ratio)))
      }
      return out
    }))
  }

  const save = async () => {
    if (anyKOvers || saving) return
    // Сначала проверяем цену в ⭐ на клиенте: пусто/0 — авто, иначе целое
    // 1..10000. Так неверное значение не уходит на сервер вообще.
    const starsByTier = {}
    for (const id of TIER_IDS) {
      const parsed = parseStarsInput(draft.tiers?.[id]?.stars)
      if (!parsed.ok) {
        setSaveError(starsErrorText(id))
        return
      }
      starsByTier[id] = parsed.value
    }
    setSaving(true); setSaveError(''); setSaved(false)
    const cleanPrizes = (list) => (list || []).map(p => ({
      id: p.id,
      kind: p.kind,
      k: Number(p.k) || 0,
      w: Number(p.w) || 0,
      enabled: p.enabled !== false,
      jack: !!p.jack,
      ...(p.kind === 'money'
        ? { usd: Number(p.usd) || 0 }
        : { days: parseInt(p.days, 10) || 0 }),
    }))
    const payload = {
      enabled: !!draft.enabled,
      introEnabled: !!draft.introEnabled,
      reserveThresholdStars: parseInt(draft.reserveThresholdStars, 10) || 0,
      historyDays: parseInt(draft.historyDays, 10) || 30,
      tiers: Object.fromEntries(TIER_IDS.map(id => {
        const t = draft.tiers?.[id] || {}
        return [id, {
          usd: Number(t.usd) || 0,
          share: Number(t.share) || 0,
          enabled: t.enabled !== false,
          // Пусто или 0 — цена в ⭐ считается из долларов (обычный режим).
          stars: starsByTier[id],
          // Свой список призов колеса.
          prizes: cleanPrizes(t.prizes),
        }]
      })),
    }
    try {
      await saveAdminFortune(payload)
      setSaved(true)
      reload()
    } catch (e) {
      setSaveError(humanSaveError(e?.message))
    } finally {
      setSaving(false)
    }
  }

  const retry = async () => {
    setRetrying(true); setRetryNote('')
    try {
      const r = await retryFortunePayouts()
      setRetryNote(`Выплачено: ${r?.paid ?? 0} · ждут: ${r?.pending ?? 0} · заблокировано: ${r?.blocked ?? 0}`)
      reload()
    } catch (e) {
      setRetryNote(`Ошибка: ${e?.message || e}`)
    } finally {
      setRetrying(false)
    }
  }

  const cfg = data?.config || {}
  const queue = data?.queue || { count: 0, stars: 0 }
  const byTier = Object.fromEntries((data?.byTier || []).map(r => [r.tier, r]))
  const winsByKind = Object.fromEntries((data?.wins || []).map(r => [r.kind, r]))

  const saveButton = (
    <button
      onClick={save}
      disabled={saving || anyKOvers}
      style={{
        width: '100%', padding: 14, borderRadius: 14, fontSize: 15, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        background: (saving || anyKOvers)
          ? 'rgba(255,255,255,0.08)'
          : 'linear-gradient(135deg, #34D399 0%, #10B981 100%)',
        color: (saving || anyKOvers) ? C.dim : '#fff',
        border: 'none', cursor: (saving || anyKOvers) ? 'default' : 'pointer',
        marginTop: 12,
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <Save size={16} strokeWidth={2.5} />
      {saving ? 'Сохраняю…' : 'Сохранить настройки колеса'}
    </button>
  )

  return (
    <div>
      <ErrorLine onRetry={reload}>{error}</ErrorLine>

      {/* ── Включение ─────────────────────────────────────────────────── */}
      <Card title="Колесо фортуны" icon={Gift} accent={C.accent}>
        <Toggle
          on={!!draft.enabled}
          onChange={v => set('enabled', v)}
          label="Функция включена"
          hint="Выключено — раздел исчезает у юзеров, счета на прокрут не создаются."
        />
        <Toggle
          on={!!draft.introEnabled}
          onChange={v => set('introEnabled', v)}
          label="Окно «Розыгрыш бонусов» при входе"
          hint="Маленькое окно с кнопкой «Забрать приз». Не показывается во время онбординга."
        />
        <div style={{ paddingTop: 12 }}>
          <div style={labelStyle}>Порог резерва звёзд (⭐)</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="number"
              min="0"
              step="1"
              value={draft.reserveThresholdStars ?? 10}
              onChange={e => set('reserveThresholdStars', e.target.value)}
              style={{ ...inputStyle, width: 110 }}
            />
            <div style={{ fontSize: 11, color: C.dim, lineHeight: 1.45, flex: 1 }}>
              Если свободных ⭐ у бота меньше порога, приходит уведомление админу.
            </div>
          </div>
        </div>
        <div style={{ paddingTop: 12 }}>
          <div style={labelStyle}>Хранить историю выигрышей (дней)</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="number"
              min="1"
              max="365"
              step="1"
              value={draft.historyDays ?? 30}
              onChange={e => set('historyDays', e.target.value)}
              style={{ ...inputStyle, width: 110 }}
            />
            <div style={{ fontSize: 11, color: C.dim, lineHeight: 1.45, flex: 1 }}>
              Владелец выбрал 30 дней — старые записи удаляются автоматически.
            </div>
          </div>
        </div>
      </Card>

      {/* ── Очередь выплат ────────────────────────────────────────────── */}
      <Card title="Очередь денежных выплат" icon={Coins} accent={queue.stars > 0 ? C.warn : C.accent}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <BigStat label="Ждут выплаты" value={fmtInt(queue.count || 0)} color={queue.count ? C.warn : C.accent} />
          <BigStat label="Звёзд к возврату" value={`${fmtInt(queue.stars || 0)} ⭐`} color={queue.stars ? C.warn : C.accent} />
        </div>
        {queue.count > 0 && (
          <div style={{
            display: 'flex', gap: 8, padding: '10px 12px', marginBottom: 12,
            background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.28)',
            borderRadius: 12, fontSize: 11.5, color: C.warn, lineHeight: 1.45,
          }}>
            <AlertTriangle size={15} strokeWidth={2.5} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              Есть невыплаченные призы. Очередь разбирается автоматически каждые 10 минут:
              возвращаем звёзды по оплаченным счетам юзера. Если звёзд или зарядов не хватает —
              придёт уведомление (порог {draft.reserveThresholdStars} ⭐).
            </span>
          </div>
        )}
        <Btn small icon={RefreshCw} color={C.indigo} disabled={retrying} onClick={retry}>
          {retrying ? 'Разбираю…' : 'Разобрать очередь сейчас'}
        </Btn>
        {retryNote && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 8 }}>{retryNote}</div>}
      </Card>

      {/* ── Колёса ────────────────────────────────────────────────────── */}
      <Card title="Колёса" icon={PlayCircle} accent={C.indigo}>
        <div style={{ fontSize: 11, color: C.dim, marginBottom: 10, lineHeight: 1.45 }}>
          Цена прокрута в долларах; звёзды считаются по курсу покупки ⭐.
          Ручная цена в ⭐ важнее расчёта — поставьте 1 для тестового режима.
          «Доля возврата» — сколько от чистого дохода прокрута идёт в копилку этого колеса.
        </div>
        {TIER_IDS.map((id, i) => {
          const tier = draft.tiers?.[id] || {}
          const stat = byTier[id]
          return (
            <div key={id} style={{ padding: '10px 0', borderTop: i === 0 ? 'none' : `1px solid ${C.line}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', flex: 1 }}>
                  {TIER_LABEL[id]}
                </div>
                {stat && <Pill color={C.muted}>{stat.spins} прокр.</Pill>}
                <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.muted }}>
                  <input
                    type="checkbox"
                    checked={tier.enabled !== false}
                    onChange={e => setTier(id, 'enabled', e.target.checked)}
                    style={{ accentColor: C.accent, width: 16, height: 16 }}
                  />
                  вкл
                </label>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <div>
                  <div style={labelStyle}>Цена, $</div>
                  <input
                    type="number" min="0.01" max="100" step="0.01"
                    value={tier.usd ?? ''}
                    onChange={e => setTier(id, 'usd', e.target.value)}
                    style={numInput}
                  />
                </div>
                <div>
                  <div style={labelStyle}>Цена, ⭐ (тест)</div>
                  <input
                    type="number" min="1" max="10000" step="1"
                    placeholder="авто"
                    value={tier.stars || ''}
                    onChange={e => setTier(id, 'stars', e.target.value)}
                    style={numInput}
                  />
                </div>
                <div>
                  <div style={labelStyle}>Доля возврата</div>
                  <input
                    type="number" min="0" max="1" step="0.01"
                    value={tier.share ?? ''}
                    onChange={e => setTier(id, 'share', e.target.value)}
                    style={numInput}
                  />
                </div>
                {stat && (
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <div style={labelStyle}>Чистый доход</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.accent, paddingTop: 7 }}>
                      ${fmtMoney(stat.netUsd)}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ fontSize: 10.5, color: C.dim, marginTop: 6 }}>
                {Number(tier.stars) > 0
                  ? `Прокрут стоит ровно ${Number(tier.stars)} ⭐ — тестовый режим.`
                  : `Без ручной цены: ≈ ${Math.max(1, Math.round((Number(tier.usd) || 0) / (data?.rate?.buyerUsdPerStar || 0.0199)))} ⭐ за прокрут.`}
              </div>
            </div>
          )
        })}
      </Card>

      {/* ── Призы ─────────────────────────────────────────────────────── */}
      <Card
        title="Призы"
        icon={Gift}
        accent={!anyKOvers ? C.accent : C.danger}
        right={<Pill color={!anyKOvers ? C.accent : C.danger}>Σ k = {sumK}</Pill>}
      >
        <div style={{ fontSize: 11, color: C.dim, marginBottom: 8, lineHeight: 1.5 }}>
          У каждого колеса — <b style={{ color: C.muted }}>свой список призов</b>:
          выберите колесо и отредактируйте только его награды. Пока своего списка нет,
          среднее и продвинутое повторяют призы базового с бо́льшими сроками и суммами
          по цене колеса.
          {' '}<b style={{ color: C.muted }}>k</b> — доля копилки, которую «стоит» приз.
          {' '}<b style={{ color: C.muted }}>Вес</b> — относительный шанс среди доступных призов.
          Сумма k включённых призов каждого колеса должна быть не больше {K_SUM_MAX} — тогда
          выплаты не превышают долю возврата и минуса не будет.
        </div>
        <Chips
          options={TIER_IDS.map(id => ({
            key: id,
            label: `${TIER_LABEL[id]} · Σk ${sumByTier[id]}`,
            count: (draft.tiers?.[id]?.prizes || []).length,
          }))}
          value={prizeTier}
          onChange={setPrizeTier}
        />
        {prizeTier !== 'base' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0 2px' }}>
            <Btn small ghost color={C.muted} onClick={scaleFromBase}>
              Призы по цене колеса
            </Btn>
            <span style={{ fontSize: 10.5, color: C.dim, lineHeight: 1.35 }}>
              взять призы базового колеса и умножить сроки и суммы на цену этого
            </span>
          </div>
        )}
        {!kOk && (
          <div style={{
            display: 'flex', gap: 8, padding: '9px 11px', marginBottom: 10,
            background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)',
            borderRadius: 10, fontSize: 11.5, color: C.danger, lineHeight: 1.45,
          }}>
            <AlertTriangle size={15} strokeWidth={2.5} style={{ flexShrink: 0, marginTop: 1 }} />
            Сумма k колеса «{TIER_LABEL[prizeTier]}» = {sumK} больше {K_SUM_MAX}. Уменьшите k
            или выключите часть призов — иначе сохранение отклонится.
          </div>
        )}
        {kOk && anyKOvers && (
          <div style={{
            display: 'flex', gap: 8, padding: '9px 11px', marginBottom: 10,
            background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)',
            borderRadius: 10, fontSize: 11.5, color: C.danger, lineHeight: 1.45,
          }}>
            <AlertTriangle size={15} strokeWidth={2.5} style={{ flexShrink: 0, marginTop: 1 }} />
            Перебор k на другом колесе:
            {' '}{TIER_IDS.filter(id => sumByTier[id] > K_SUM_MAX + 1e-9)
              .map(id => `${TIER_LABEL[id]} (${sumByTier[id]})`).join(', ')}.
            Переключитесь на него и поправьте — сохранение заблокировано, пока перебор есть.
          </div>
        )}

        {prizeList.map((p, idx) => {
          const jackDisabled = p.kind !== 'money'
          return (
            <div key={p.id} style={{ padding: '10px 0', borderTop: idx === 0 ? 'none' : `1px solid ${C.line}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                <select
                  value={p.kind}
                  onChange={e => {
                    const kind = e.target.value
                    patchPrize(idx, 'kind', kind)
                    if (kind === 'money' && !p.usd) patchPrize(idx, 'usd', 5)
                    if (kind !== 'money' && !p.days) patchPrize(idx, 'days', 1)
                    if (kind !== 'money' && p.jack) patchPrize(idx, 'jack', false)
                  }}
                  style={smallSelect}
                >
                  {Object.entries(KIND_LABEL).map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </select>

                {p.kind === 'money' ? (
                  <input
                    type="number" min="0.01" max="1000" step="0.01"
                    value={p.usd ?? ''}
                    onChange={e => patchPrize(idx, 'usd', e.target.value)}
                    style={{ ...numInput, width: 90 }}
                    placeholder="$"
                  />
                ) : (
                  <input
                    type="number" min="1" max="365" step="1"
                    value={p.days ?? ''}
                    onChange={e => patchPrize(idx, 'days', e.target.value)}
                    style={{ ...numInput, width: 76 }}
                    placeholder="дн."
                  />
                )}

                <div style={{ flex: 1, fontSize: 11, color: C.dim, overflowWrap: 'anywhere' }}>
                  {prizeTitle(p)} · <span style={{ fontFamily: 'monospace' }}>{p.id}</span>
                </div>

                <button
                  onClick={() => removePrize(idx)}
                  disabled={prizeList.length <= 1}
                  aria-label="Удалить приз"
                  style={{
                    width: 30, height: 30, borderRadius: 9, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.line}`,
                    cursor: prizeList.length <= 1 ? 'default' : 'pointer',
                    opacity: prizeList.length <= 1 ? 0.4 : 1,
                  }}
                >
                  <Trash2 size={14} color={C.danger} />
                </button>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
                <div>
                  <div style={labelStyle}>k</div>
                  <input
                    type="number" min="0" max="1" step="0.001"
                    value={p.k ?? ''}
                    onChange={e => patchPrize(idx, 'k', e.target.value)}
                    style={numInput}
                  />
                </div>
                <div>
                  <div style={labelStyle}>Вес</div>
                  <input
                    type="number" min="0.01" max="100" step="0.1"
                    value={p.w ?? ''}
                    onChange={e => patchPrize(idx, 'w', e.target.value)}
                    style={numInput}
                  />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: C.muted, paddingBottom: 8 }}>
                  <input
                    type="checkbox"
                    checked={p.enabled !== false}
                    onChange={e => patchPrize(idx, 'enabled', e.target.checked)}
                    style={{ accentColor: C.accent, width: 16, height: 16 }}
                  />
                  включён
                </label>
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5,
                  color: jackDisabled ? C.dim : '#E9B949', paddingBottom: 8,
                  opacity: jackDisabled ? 0.5 : 1,
                }}>
                  <input
                    type="checkbox"
                    checked={!!p.jack}
                    disabled={jackDisabled}
                    onChange={e => patchPrize(idx, 'jack', e.target.checked)}
                    style={{ accentColor: '#E9B949', width: 16, height: 16 }}
                  />
                  джекпот
                </label>
              </div>
            </div>
          )
        })}

        <div style={{ marginTop: 10 }}>
          <Btn small icon={Plus} color={C.accent} disabled={prizeList.length >= 30} onClick={addPrize}>
            Добавить приз
          </Btn>
        </div>
      </Card>

      {/* ── Статистика ────────────────────────────────────────────────── */}
      <Card title="Статистика" icon={Coins} accent={C.accent}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <BigStat label="Прокрутов" value={fmtInt(data?.totals?.spins)} />
          <BigStat label="Звёзд получено" value={fmtInt(data?.totals?.stars)} color={C.indigo} />
          <BigStat label="Чистый доход" value={`$${fmtMoney(data?.totals?.netUsd)}`} />
        </div>
        <Row
          label="Денежные призы"
          value={`${fmtInt(winsByKind.money?.n)} · $${fmtMoney(winsByKind.money?.usd)}`}
        />
        <Row
          label="Подписки"
          value={`${fmtInt((winsByKind.demo?.n || 0) + (winsByKind.real?.n || 0))} шт.`}
          valueSub={`демо ${fmtInt(winsByKind.demo?.n)} · реал ${fmtInt(winsByKind.real?.n)}`}
        />
        <div style={{ fontSize: 10.5, color: C.dim, marginTop: 6 }}>
          Период — последние {cfg.historyDays} дн. (срок хранения истории).
        </div>
      </Card>

      {/* ── Последние выигрыши ────────────────────────────────────────── */}
      <Card title="Последние выигрыши" icon={Gift} accent={C.indigo}>
        {(!data?.recent || data.recent.length === 0) ? (
          <Empty>Выигрышей пока нет.</Empty>
        ) : (
          data.recent.map((r, i) => (
            <div key={r.id} style={{ padding: '8px 0', borderTop: i === 0 ? 'none' : `1px solid ${C.line}` }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <div style={{ fontSize: 11, color: C.dim, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                  {fmtDateTime(r.createdAt)}
                </div>
                <div style={{ fontSize: 12.5, color: '#fff', flex: 1, overflowWrap: 'anywhere' }}>
                  {r.name || `id ${r.userId}`} · {TIER_LABEL[r.tier] || r.tier}
                </div>
                <Pill color={STATUS_COLOR[r.status] || C.muted}>
                  {r.status === 'paid' ? 'выплачено' : r.status === 'pending' ? 'в очереди' : r.status === 'done' ? 'начислено' : r.status}
                </Pill>
              </div>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                {r.kind === 'money'
                  ? `$${r.usd} звёздами · ${fmtInt(r.refundedStars)} / ${fmtInt(r.stars)} ⭐`
                  : `+${r.days} дн. · ${r.kind === 'real' ? 'реальная' : 'демо'} подписка`}
              </div>
            </div>
          ))
        )}
      </Card>

      {/* ── Выплаты ───────────────────────────────────────────────────── */}
      <Card title="Выплаты звёзд" icon={Coins} accent={queue.count ? C.warn : C.muted}>
        {(!data?.payouts || data.payouts.length === 0) ? (
          <Empty>Денежных выплат пока нет.</Empty>
        ) : (
          data.payouts.map((p, i) => (
            <div key={p.id} style={{ padding: '8px 0', borderTop: i === 0 ? 'none' : `1px solid ${C.line}` }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <div style={{ fontSize: 11, color: C.dim, flexShrink: 0 }}>{fmtDateTime(p.createdAt)}</div>
                <div style={{ fontSize: 12.5, color: '#fff', flex: 1, overflowWrap: 'anywhere' }}>
                  {p.name || `id ${p.userId}`} · ${p.usd}
                </div>
                <Pill color={STATUS_COLOR[p.status] || C.muted}>
                  {p.status === 'paid' ? 'выплачено' : p.status === 'pending' ? 'в очереди' : p.status}
                </Pill>
              </div>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                Возвращено {fmtInt(p.refundedStars)} / {fmtInt(p.targetStars)} ⭐
                {p.error ? ` · ${p.error}` : ''}
              </div>
            </div>
          ))
        )}
      </Card>

      {saveButton}
      {saved && (
        <div style={{ textAlign: 'center', fontSize: 13, color: C.accent, marginTop: 8 }}>
          Сохранено
        </div>
      )}
      <ErrorLine>{saveError}</ErrorLine>
      <div style={{ fontSize: 10.5, color: C.dim, lineHeight: 1.5, marginTop: 4 }}>
        Изменения применяются сразу после сохранения: новый конфиг читают и API, и бот.
        Сумма k проверяется на сервере ещё раз.
      </div>
    </div>
  )
}
